/**
 * What a chat message body is made of — V10 §10 follow-up.
 *
 * Pulled out of `ChatPage` as a pure function for the same reason
 * `composer.ts` is pure: the composer writes these markers and this reads them
 * back, and the pair is only correct if both agree. A tokenizer tested through
 * a rendered bubble is a tokenizer tested by eye.
 *
 * ## The bug this exists to end
 *
 * The old tokenizer was one regex, and `` `[^`]+` `` matches **across
 * newlines**. A fenced block, which is what the toolbar's code-block button
 * writes —
 *
 * ```text
 * ```
 * ada
 * ```
 * ```
 *
 * — was therefore read as: two literal backticks, then an inline code span
 * containing `"\nada\n"`, then two more literal backticks. That is exactly what
 * the founder's screenshot shows: a stray glyph, a chip, a stray glyph.
 *
 * Fences are consumed **first** now, so nothing inside one is re-tokenised and
 * nothing outside one can swallow one.
 */

/** One run of body text, already classified. */
export type BodySegment =
  | { kind: 'text'; value: string }
  | { kind: 'bold'; value: string }
  | { kind: 'italic'; value: string }
  | { kind: 'code'; value: string }
  | { kind: 'codeBlock'; value: string }
  | { kind: 'link'; value: string }
  | { kind: 'mention'; value: string };

const MENTION = /@\[([^\]]{1,80})\]\(([a-f0-9]{24})\)/g;

/** `@[Ada Lovelace](65f…)` → `@Ada Lovelace`, before anything else looks at it. */
export function renderMentions(text: string): string {
  return text.replace(MENTION, (_full, name: string) => `@${name}`);
}

/**
 * A closed fence, and only a closed one.
 *
 * An unterminated ``` is left as literal text: somebody mid-sentence has not
 * asked for a code block yet, and swallowing the rest of their message to the
 * end would be a worse guess than showing the backticks they typed.
 */
const FENCE = /```(?:[a-z]*)\n?([\s\S]*?)\n?```/gi;

/**
 * Inline markers, in one pass. Deliberately does not know about fences.
 *
 * An inline span may **not cross a newline** — that is what let the old
 * tokenizer swallow a whole fenced block.
 */
const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`\n]+`|@[\w .'-]+|https?:\/\/\S+)/g;

/**
 * The anchored twin of each alternative above.
 *
 * `split` hands back the **gaps** as well as the matches, so a classifier that
 * sniffs the first and last character relabels a gap that merely happens to
 * start and end with a marker. A body of `` `a⏎b` `` is text — the pattern
 * above refuses it, because a span may not cross a newline — and a
 * `startsWith`/`endsWith` check called it code anyway. The splitter and the
 * classifier have to be the same rule, so they are.
 */
const IS_BOLD = /^\*\*[^*]+\*\*$/;
const IS_ITALIC = /^\*[^*]+\*$/;
const IS_CODE = /^`[^`\n]+`$/;
const IS_LINK = /^https?:\/\/\S+$/;
const IS_MENTION = /^@[\w .'-]+$/;

function inlineSegments(text: string): BodySegment[] {
  return text
    .split(INLINE)
    .filter(Boolean)
    .map((token): BodySegment => {
      if (IS_BOLD.test(token)) return { kind: 'bold', value: token.slice(2, -2) };
      if (IS_ITALIC.test(token)) return { kind: 'italic', value: token.slice(1, -1) };
      if (IS_CODE.test(token)) return { kind: 'code', value: token.slice(1, -1) };
      if (IS_LINK.test(token)) return { kind: 'link', value: token };
      if (IS_MENTION.test(token)) return { kind: 'mention', value: token };
      return { kind: 'text', value: token };
    });
}

/** The whole body, fences first and everything else inside the gaps. */
export function parseMessageBody(raw: string): BodySegment[] {
  const text = renderMentions(raw);
  const segments: BodySegment[] = [];

  let lastIndex = 0;
  FENCE.lastIndex = 0;
  let match = FENCE.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      segments.push(...inlineSegments(text.slice(lastIndex, match.index)));
    }
    segments.push({ kind: 'codeBlock', value: match[1] });
    lastIndex = match.index + match[0].length;
    match = FENCE.exec(text);
  }

  if (lastIndex < text.length) segments.push(...inlineSegments(text.slice(lastIndex)));
  return segments;
}
